import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Image,
  Icon,
  AspectRatio,
  IconButton,
  Circle,
  Button,
  Link,
  chakra,
} from "@chakra-ui/react";
import {
  FaThumbsUp,
  FaComment,
  FaShare,
  FaChevronLeft,
  FaChevronRight,
  FaEllipsisH,
  FaRegThumbsUp,
  FaRegComment,
  FaRegPaperPlane,
} from "react-icons/fa";
import { FaCheckCircle } from "react-icons/fa";
import type { AdPreviewCollaboration } from "../../utils/adPreviewCollaboration";
import CollaborationIdentityDisplay from "./CollaborationIdentityDisplay";

const Video = chakra("video");

// Helper function to determine if a URL is a video
const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};
export interface FacebookFeedPreviewProps {
  message: string;
  ctaText: string;
  mediaUrls: string[];
  mediaType: "single" | "carousel";
  pageName: string;
  profilePictureUrl?: string;
  websiteUrl?: string;
  eventName: string;
  description: string;
  collaboration?: AdPreviewCollaboration;
  fullWidth?: boolean;
}

const FacebookFeedPreview: React.FC<FacebookFeedPreviewProps> = ({
  message,
  ctaText,
  eventName,
  description,
  mediaUrls,
  mediaType,
  pageName,
  profilePictureUrl,
  websiteUrl,
  collaboration,
  fullWidth = false,
}) => {
  const profilePicture = profilePictureUrl; // Facebook pages don't have profile pictures in this context
  const displayUrl = websiteUrl ? new URL(websiteUrl).hostname : "";
  const headline = eventName;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  // Text truncation settings
  const MAX_LINES = 2;
  const MAX_CHARACTERS = 120;
  const actualMessage = message || "Ad primary text";

  // Calculate if text should be truncated based on lines and characters
  const lines = actualMessage.split("\n");
  const shouldTruncateByLines = lines.length > MAX_LINES;
  const shouldTruncateByChars = actualMessage.length > MAX_CHARACTERS;
  const shouldTruncate = shouldTruncateByLines || shouldTruncateByChars;

  const displayText = (() => {
    if (!shouldTruncate || isTextExpanded) {
      return actualMessage;
    }

    if (shouldTruncateByLines) {
      // Truncate by lines first
      const truncatedLines = lines.slice(0, MAX_LINES);
      return truncatedLines.join("\n") + "...";
    } else {
      // Truncate by characters
      return actualMessage.substring(0, MAX_CHARACTERS) + "...";
    }
  })();

  // Use the mediaUrls array directly - component determines if it's carousel based on length
  const mediaItems = mediaUrls || [];
  const currentMedia = mediaItems[currentImageIndex];
  const isCarousel = mediaItems.length > 1;

  // Reset current image index when media changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [mediaItems]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? mediaItems.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) =>
      prev === mediaItems.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      overflow="hidden"
      w="full"
      boxShadow="lg"
      maxW={fullWidth ? "100%" : ["100%", "70%"]}
    >
      {/* Header */}
      <HStack p={4} spacing={3} alignItems="center">
        <Box flex={1} minW={0}>
          <CollaborationIdentityDisplay
            platform="facebook"
            primaryName={pageName}
            primaryProfilePictureUrl={profilePicture}
            collaboration={collaboration}
            secondaryLabel={collaboration ? undefined : "Sponsored"}
            size="lg"
          />
        </Box>
        {!collaboration && (
          <Icon
            as={FaCheckCircle}
            color="blue.500"
            fontSize="xs"
            flexShrink={0}
          />
        )}
        <Icon as={FaEllipsisH} color="gray.600" fontSize="sm" flexShrink={0} />
      </HStack>

      {/* Primary Text */}
      <Box px={4} pb={3} wordBreak="break-word" overflowWrap="break-word">
        <Text
          fontSize="sm"
          color="black"
          lineHeight="1.4"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          overflowWrap="break-word"
        >
          {displayText}
        </Text>
        {shouldTruncate && (
          <Link
            fontSize="xs"
            color="gray.600"
            cursor="pointer"
            onClick={() => setIsTextExpanded(!isTextExpanded)}
            _hover={{ textDecoration: "underline" }}
            display="block"
            mt={1}
          >
            {isTextExpanded ? "Show less" : "Show more"}
          </Link>
        )}
      </Box>

      {/* Media Content */}
      {isCarousel ? (
        // Gallery-style carousel with partial next card visible
        <Box position="relative" overflow="hidden" p={4} pt={1} w="full">
          <HStack
            spacing={0}
            transform={`translateX(-${currentImageIndex * 50}%)`}
            transition="transform 0.3s ease-in-out"
            w={`${mediaItems.length * 90}%`}
            alignItems="stretch"
          >
            {mediaItems.map((media, index) => (
              <Box
                key={index}
                w={`${100 / mediaItems.length}%`}
                // h="full"
                position="relative"
                flexShrink={0}
                bg="gray.100"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="2xl"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                mr={2}
              >
                {/* Image */}
                <Box flex={1} position="relative" overflow="hidden">
                  {(() => {
                    if (!media) {
                      return (
                        <Box
                          w="full"
                          h="full"
                          bg="gray.300"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text color="gray.500" fontSize="sm">
                            Carousel Item {index + 1}
                          </Text>
                        </Box>
                      );
                    }

                    return media ? (
                      isVideoUrl(media) ? (
                        <Video
                          aspectRatio="1/1"
                          src={media}
                          w="full"
                          h="full"
                          objectFit="cover"
                          bg="white"
                          autoPlay
                          muted
                          loop
                        />
                      ) : (
                        <Image
                          aspectRatio="1/1"
                          src={media}
                          alt={`Facebook carousel item ${index + 1}`}
                          w="full"
                          h="full"
                          objectFit="cover"
                          bg="white"
                        />
                      )
                    ) : (
                      <Box
                        w="full"
                        minH="200px"
                        aspectRatio="1/1"
                        bg="gray.300"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.500" fontSize="sm">
                          Carousel Item {index + 1}
                        </Text>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Card Content */}
                <Box p={3} flexShrink={0}>
                  <HStack
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0}
                    flexShrink={1}
                  >
                    <VStack
                      alignItems="flex-start"
                      gap={0}
                      justifyContent="stretch"
                      flexShrink={1}
                      flexWrap="wrap"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="black"
                        flexShrink={1}
                      >
                        {eventName}
                      </Text>
                      <Text
                        fontSize="xs"
                        color="gray.600"
                        noOfLines={2}
                        flexWrap="wrap"
                        flexShrink={1}
                      >
                        {description}
                      </Text>
                    </VStack>
                    <Button
                      maxW="40%"
                      flexShrink={0}
                      size="sm"
                      bg="gray.200"
                      color="black"
                      borderRadius="md"
                      fontSize="xs"
                      fontWeight="bold"
                      _hover={{ bg: "gray.200" }}
                    >
                      {ctaText}
                    </Button>
                  </HStack>
                </Box>
              </Box>
            ))}
          </HStack>

          {/* Navigation Arrows */}
          {mediaItems.length > 1 && (
            <>
              <IconButton
                aria-label="Previous image"
                icon={<FaChevronLeft />}
                position="absolute"
                left="2"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                bg="rgba(0, 0, 0, 0.6)"
                color="white"
                borderRadius="full"
                _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                onClick={handlePrevious}
                zIndex={1}
              />

              <IconButton
                aria-label="Next image"
                icon={<FaChevronRight />}
                position="absolute"
                right="2"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                bg="rgba(0, 0, 0, 0.6)"
                color="white"
                borderRadius="full"
                _hover={{ bg: "rgba(0, 0, 0, 0.8)" }}
                onClick={handleNext}
                zIndex={1}
              />
            </>
          )}
        </Box>
      ) : (
        // Single Image Layout
        <AspectRatio ratio={16 / 9}>
          <Box bg="gray.100" position="relative">
            {currentMedia ? (
              isVideoUrl(currentMedia) ? (
                <Video
                  src={currentMedia}
                  w="full"
                  h="full"
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <Image
                  src={currentMedia}
                  alt="Facebook post content"
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              )
            ) : (
              <Box
                w="full"
                h="full"
                bg="gray.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text color="gray.500" fontSize="sm">
                  Image/Video
                </Text>
              </Box>
            )}
          </Box>
        </AspectRatio>
      )}

      {/* Link Preview - Only show for single images */}
      {!isCarousel && (
        <Box border="1px solid" borderColor="gray.200" borderTop="none" p={3}>
          <HStack spacing={3} alignItems="center" w="full" minW={0}>
            <VStack
              spacing={1}
              alignItems="flex-start"
              flex={1}
              flexShrink={1}
              minW={0}
              overflow="hidden"
            >
              <Text
                fontSize="xs"
                color="gray.600"
                noOfLines={2}
                wordBreak="break-all"
                overflowWrap="break-word"
                whiteSpace="normal"
              >
                {displayUrl}
              </Text>
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="black"
                lineHeight="1.3"
                wordBreak="break-word"
                overflowWrap="break-word"
              >
                {headline}
              </Text>
            </VStack>
            <Button
              size="sm"
              bg="gray.100"
              color="black"
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              _hover={{ bg: "gray.200" }}
              px={4}
              flexShrink={0}
            >
              {ctaText}
            </Button>
          </HStack>
        </Box>
      )}

      {/* Interaction Bar */}
      <HStack
        p={3}
        px={4}
        justifyContent="space-between"
        alignItems="center"
        borderTop="1px solid"
        borderColor="gray.200"
      >
        <HStack spacing={4} w="full" justifyContent="space-between">
          <HStack spacing={1} alignItems="center" cursor="pointer">
            <Icon as={FaRegThumbsUp} color="gray.600" fontSize="sm" />
            <Text fontSize="sm" color="gray.600">
              Like
            </Text>
          </HStack>
          <HStack spacing={1} alignItems="center" cursor="pointer">
            <Icon as={FaRegComment} color="gray.600" fontSize="sm" />
            <Text fontSize="sm" color="gray.600">
              Comment
            </Text>
          </HStack>
          <HStack spacing={1} alignItems="center" cursor="pointer">
            <Icon as={FaRegPaperPlane} color="gray.600" fontSize="sm" />
            <Text fontSize="sm" color="gray.600">
              Share
            </Text>
          </HStack>
        </HStack>
      </HStack>
    </Box>
  );
};

export default FacebookFeedPreview;
